/**
 * One way for the CRM buttons that read partner documents — AI Notes "Run AI
 * Analysis", "Build Partner Profile Summary" and "AI Verify Banking" — to wait
 * for their answer and tell the person what actually happened.
 *
 * Each button used to wait on a single request while the server read every
 * chosen document. A scanned document takes minutes, the server gives up on a
 * request after five, and a document it could not read simply dropped out while
 * the screen still said "completed successfully". The button now gets back a
 * queued run straight away; this checks the run every 5 seconds while it is
 * queued or processing, and hands the finished run back with one record per
 * chosen document (document_id, filename, status 'read' | 'unreadable',
 * message) so the screen can name each document that was not read.
 *
 * The server reports a run nothing is working on any more as failed, so a
 * spinner here never outlives the worker.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReadingProgress from '../ui/ReadingProgress';
import { announceReadingStarted } from '../ui/ReadingTray';
import axiosInstance from '../../utils/axiosConfig';

export const POLL_INTERVAL_MS = 5000;

const ACTIVE_STATUSES = ['queued', 'processing'];

const GENERIC_FAILURE = 'This could not be completed. Please try again.';

/** The run is still waiting for, or being worked on by, the background service. */
export const isRunActive = (run) => Boolean(run && ACTIVE_STATUSES.includes(run.status));

/** The chosen documents that could not be read, each with its reason. */
export const unreadableDocuments = (run) => {
    const documents = (run && Array.isArray(run.documents)) ? run.documents : [];
    return documents.filter((record) => record && record.status !== 'read');
};

const nameList = (records) => records.map((record) => record.filename || 'A document').join(', ');

/**
 * What to tell the person about a finished run, as { tone, text }. tone is
 * 'success' only when the run finished AND every chosen document was read: an
 * unearned success message is exactly what this replaces.
 */
export const runOutcome = (run, successText) => {
    if (!run || isRunActive(run)) return null;
    if (run.status !== 'done') {
        return { tone: 'error', text: run.error || GENERIC_FAILURE };
    }
    const unread = unreadableDocuments(run);
    if (unread.length === 0) {
        return { tone: 'success', text: successText };
    }
    const total = Array.isArray(run.documents) ? run.documents.length : unread.length;
    const noun = total === 1 ? 'document' : 'documents';
    return {
        tone: 'warning',
        text: `Finished, but ${unread.length} of ${total} chosen ${noun} could not be read and `
            + `${unread.length === 1 ? 'was' : 'were'} left out: ${nameList(unread)}. `
            + 'The reasons are shown below.',
    };
};

/**
 * Hold a run and check back on it until it is done or failed.
 * start(run) with the 202 answer from the button's POST; onFinished(run) is
 * called once with the finished run.
 */
export const useDocumentReadingRun = (onFinished, { kind, partnerId } = {}) => {
    const [run, setRun] = useState(null);
    // The latest callback, without re-arming the timer every render.
    const finishedRef = useRef(onFinished);
    finishedRef.current = onFinished;
    const active = isRunActive(run);

    useEffect(() => {
        if (!active) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const response = await axiosInstance.get(`/api/crm/document-reading-runs/${run.run_id}/`);
                if (cancelled) return;
                setRun(response.data);
                if (!isRunActive(response.data) && finishedRef.current) {
                    finishedRef.current(response.data);
                }
            } catch (error) {
                if (cancelled) return;
                if (error?.response?.status === 404) {
                    const gone = {
                        ...run,
                        status: 'failed',
                        error: error.response.data?.error || GENERIC_FAILURE,
                    };
                    setRun(gone);
                    if (finishedRef.current) finishedRef.current(gone);
                    return;
                }
                // A missed check is simply tried again on the next tick.
                setRun((current) => (current ? { ...current } : current));
            }
        }, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [active, run]);

    // Coming back to the screen - after a refresh, from another tab, or from the
    // app-wide tray - picks up a run this person started and is still going, or
    // the run the tray's link names. The window used to forget it on close.
    useEffect(() => {
        if (!kind || !partnerId) return undefined;
        let cancelled = false;
        const resume = async () => {
            try {
                const named = new URLSearchParams(window.location.search).get('reading_run');
                let runId = named;
                if (!runId) {
                    const mine = await axiosInstance.get('/api/reading-progress/mine/');
                    const match = (mine.data?.items || []).find((item) => (
                        item.kind === 'crm_reading_run'
                        && item.run_kind === kind
                        && String(item.partner_id) === String(partnerId)
                    ));
                    runId = match?.id;
                }
                if (!runId || cancelled) return;
                const response = await axiosInstance.get(`/api/crm/document-reading-runs/${runId}/`);
                if (cancelled || response.data?.kind !== kind) return;
                setRun((current) => current || response.data);
                if (!isRunActive(response.data) && named && finishedRef.current) {
                    finishedRef.current(response.data);
                }
            } catch (error) {
                // Nothing to pick up is the normal case; a failed look is not worth a message.
            }
        };
        resume();
        return () => { cancelled = true; };
    }, [kind, partnerId]);

    const start = useCallback((queuedRun) => {
        setRun(queuedRun);
        if (queuedRun && isRunActive(queuedRun)) announceReadingStarted();
        if (queuedRun && !isRunActive(queuedRun) && finishedRef.current) {
            finishedRef.current(queuedRun);
        }
    }, []);
    const clear = useCallback(() => setRun(null), []);

    return { run, active, start, clear };
};

const PANEL_TONES = {
    active: { background: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
    warning: { background: '#fffbeb', border: '#fcd34d', color: '#92400e' },
    error: { background: '#fef2f2', border: '#fca5a5', color: '#991b1b' },
};

/**
 * What the person sees under the button: that the documents are being read,
 * or — once finished — which documents could not be read and why. Draws
 * nothing for a run that read everything it was given.
 */
export const DocumentReadingRunPanel = ({ run, activeText, onDismiss }) => {
    if (!run) return null;
    const active = isRunActive(run);
    const unread = unreadableDocuments(run);
    const failed = !active && run.status !== 'done';
    if (!active && !failed && unread.length === 0) return null;

    const tone = PANEL_TONES[active ? 'active' : (failed ? 'error' : 'warning')];
    return (
        <div
            role={failed ? 'alert' : 'status'}
            data-testid="document-reading-run-panel"
            style={{
                marginTop: '0.75rem',
                padding: '0.75rem 1rem',
                backgroundColor: tone.background,
                border: `1px solid ${tone.border}`,
                borderRadius: '0.5rem',
                color: tone.color,
                fontSize: '0.875rem',
                lineHeight: 1.5,
            }}
        >
            {active && (
                <div>
                    <div style={{ marginBottom: '0.5rem' }}>
                        {activeText || 'Reading the chosen documents. Scanned documents can take several minutes; this updates by itself.'}
                    </div>
                    <ReadingProgress
                        compact
                        progress={run.progress}
                        startedAt={run.started_at}
                        leaveText="You can leave this tab — it carries on and shows the result when you come back."
                    />
                </div>
            )}
            {failed && <div style={{ fontWeight: 600 }}>{run.error || GENERIC_FAILURE}</div>}
            {!active && unread.length > 0 && (
                <div style={{ marginTop: failed ? '0.5rem' : 0 }}>
                    {!failed && (
                        <div style={{ fontWeight: 600 }}>
                            {unread.length === 1 ? 'This document could not be read and was left out:' : 'These documents could not be read and were left out:'}
                        </div>
                    )}
                    <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                        {unread.map((record, index) => (
                            <li key={`${record.document_id}-${index}`}>
                                <strong>{record.filename || 'A document'}</strong>
                                {record.message ? ` — ${record.message}` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {!active && onDismiss && (
                <button
                    type="button"
                    onClick={onDismiss}
                    style={{
                        marginTop: '0.5rem',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: tone.color,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                    }}
                >
                    Dismiss
                </button>
            )}
        </div>
    );
};
